// Indexer ops dashboard. Read-only surfacing of the indexer pipeline's health:
//  - Now:        live SQS queue depths + ECS task counts (auto-refresh ~15s)
//  - SLO:        push -> searchable; the headline "% of small commits < 5 min"
//  - Throughput: jobs/hr over the window (line chart)
//  - Phases:     where time goes — avg ms per phase (bar chart)
//  - Recent:     the last N index runs (table)
//
// Backed by the three read-only endpoints in indexer/src/code_rag/web_server.py:
//   /api/queues, /api/index-stats, /api/index-runs.

import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { getIndexRuns, getIndexStats, getQueues } from '../lib/api';
import type { Theme } from '../lib/shiki';

interface Props {
  theme: Theme;
}

/** ms -> compact human string; null/undefined -> em dash. */
function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

export function DashboardPage({ theme }: Props) {
  // The "Now" panel polls; history/stats are fetched once per mount (cheap, can be refreshed
  // by switching tabs / reloading). 15s matches the queue cache TTL comfortably.
  const queues = useQuery({ queryKey: ['queues'], queryFn: getQueues, refetchInterval: 15000 });
  const stats = useQuery({ queryKey: ['index-stats'], queryFn: () => getIndexStats('24h') });
  const runs = useQuery({ queryKey: ['index-runs'], queryFn: () => getIndexRuns(50) });

  const q = queues.data;
  const s = stats.data;
  const rows = runs.data?.runs ?? [];

  // Chart colors keyed off theme so axes/grid read on both backgrounds.
  const axisColor = theme === 'dark' ? '#71717a' : '#a1a1aa';
  const gridColor = theme === 'dark' ? '#27272a' : '#e4e4e7';
  const lineColor = '#6366f1'; // indigo-500
  const barColor = '#f59e0b'; // amber-500

  const phaseData = s
    ? Object.entries(s.phase_avg_ms).map(([phase, ms]) => ({ phase, ms: Math.round(ms) }))
    : [];

  return (
    <div className="flex-1 min-w-0 overflow-y-auto px-4 py-4 space-y-6">
      {/* NOW */}
      <section>
        <h2 className="text-sm font-semibold mb-2">Now</h2>
        {queues.isError && (
          <div className="text-sm text-rose-600">
            Queue stats unavailable: {(queues.error as Error).message}
          </div>
        )}
        {q && (
          <div className="flex flex-wrap gap-3 text-sm">
            <Stat
              label="cpu-jobs"
              value={`${q.queues['cpu-jobs']?.visible ?? 0} (+${q.queues['cpu-jobs']?.in_flight ?? 0})`}
            />
            <Stat
              label="gpu-jobs"
              value={`${q.queues['gpu-jobs']?.visible ?? 0} (+${q.queues['gpu-jobs']?.in_flight ?? 0})`}
            />
            <Stat
              label="cpu tasks"
              value={`${q.services['cpu-indexer']?.running ?? 0}/${q.services['cpu-indexer']?.desired ?? 0}`}
            />
            <Stat
              label="gpu tasks"
              value={`${q.services['gpu-indexer']?.running ?? 0}/${q.services['gpu-indexer']?.desired ?? 0}`}
            />
            <Stat
              label="DLQ"
              value={q.dlq_nonempty ? 'NON-EMPTY' : 'clean'}
              danger={q.dlq_nonempty}
            />
          </div>
        )}
      </section>

      {/* SLO */}
      {s && (
        <section>
          <h2 className="text-sm font-semibold mb-2">Push → searchable (5-min SLO)</h2>
          <div className="flex flex-wrap gap-6 items-baseline">
            <div className="text-3xl font-bold tabular-nums">
              {s.slo_pct_under_5min.toFixed(0)}%
            </div>
            <div className="text-sm text-zinc-500 dark:text-zinc-400">
              small commits &lt; 5 min · p50 e2e {fmtMs(s.p50_e2e_ms)} · p95 {fmtMs(s.p95_e2e_ms)} ·
              queue-wait p95 {fmtMs(s.p95_queue_ms)}
            </div>
          </div>
        </section>
      )}

      {/* THROUGHPUT */}
      {s && (
        <section>
          <h2 className="text-sm font-semibold mb-2">Throughput (jobs/hr)</h2>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={s.throughput}>
              <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
              <XAxis dataKey="bucket" hide />
              <YAxis stroke={axisColor} fontSize={12} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="jobs" stroke={lineColor} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* PHASES */}
      {s && (
        <section>
          <h2 className="text-sm font-semibold mb-2">Where time goes (avg ms)</h2>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={phaseData}>
              <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
              <XAxis dataKey="phase" stroke={axisColor} fontSize={12} />
              <YAxis stroke={axisColor} fontSize={12} />
              <Tooltip />
              <Bar dataKey="ms" fill={barColor} />
            </BarChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* RECENT JOBS */}
      <section>
        <h2 className="text-sm font-semibold mb-2">Recent jobs</h2>
        {runs.isError && (
          <div className="text-sm text-rose-600">
            Runs unavailable: {(runs.error as Error).message}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-zinc-500 dark:text-zinc-400">
                <th className="py-1 pr-3 font-medium">ts</th>
                <th className="py-1 pr-3 font-medium">repo</th>
                <th className="py-1 pr-3 font-medium">branch</th>
                <th className="py-1 pr-3 font-medium">mode</th>
                <th className="py-1 pr-3 font-medium">dev</th>
                <th className="py-1 pr-3 font-medium">status</th>
                <th className="py-1 pr-3 font-medium text-right">chunks</th>
                <th className="py-1 pr-3 font-medium text-right">emb/s</th>
                <th className="py-1 pr-3 font-medium text-right">e2e</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-zinc-200 dark:border-zinc-800">
                  <td className="py-1 pr-3 font-mono whitespace-nowrap">{r.ts.slice(5, 16)}</td>
                  <td className="py-1 pr-3">{r.repo ?? '—'}</td>
                  <td className="py-1 pr-3 font-mono">{r.branch ?? '—'}</td>
                  <td className="py-1 pr-3">{r.mode ?? '—'}</td>
                  <td className="py-1 pr-3">{r.device ?? '—'}</td>
                  <td
                    className={`py-1 pr-3 ${
                      r.status === 'failed'
                        ? 'text-rose-600'
                        : r.status === 'ok'
                          ? 'text-emerald-600'
                          : 'text-zinc-500'
                    }`}
                  >
                    {r.status ?? '—'}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">{r.chunks_written ?? '—'}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">
                    {r.emb_per_sec != null ? r.emb_per_sec.toFixed(1) : '—'}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">{fmtMs(r.end_to_end_ms)}</td>
                </tr>
              ))}
              {rows.length === 0 && !runs.isLoading && (
                <tr>
                  <td colSpan={9} className="py-2 text-zinc-500">
                    No runs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div
      className={`px-3 py-2 rounded border ${
        danger
          ? 'border-rose-500 text-rose-600'
          : 'border-zinc-300 dark:border-zinc-700'
      }`}
    >
      <div className="text-xs text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="font-mono">{value}</div>
    </div>
  );
}
